import { useCallback, useEffect, useState } from "react";
import { useGetClientRequiresAuthedIntroRoom as useGetClientRequiresAuthedIntroRoomBase } from "../auth/useClientRequiresAuthedIntroRoom";
import { useAuth0 as useAuth0Base } from "@auth0/auth0-react";
import { EventTypeEnum } from "../../../segment/types";
import { useAutoRetryOnError as useAutoRetryOnErrorBase } from "../../../useAutoRetryOnError";
import { useTrackIntroRoomEvent as useTrackIntroRoomEventBase } from "./useTrackIntroRoomEvent";
import { useParams as useParamsBase } from "react-router-dom";
import { logger as loggerBase } from "../../../datadog";
import { useFeatureGate as useFeatureGateBase } from "@statsig/react-bindings";
import {
  useGetClientUserMissingConsentsQuery as useGetClientUserMissingConsentsQueryBase,
  useGetClientUserWithParentGuardianInformation as useGetClientUserWithParentGuardianInformationBase,
} from "@grow-therapy-team/seedling-components";
import { useIntroRoomData } from "./useIntroRoomData";

export type IntroRoomState =
  | "loading"
  | "default"
  | "piiVerification"
  | "requiredAgreementsReview";

export type Dependencies = {
  logger: typeof loggerBase;
  useAuth0: typeof useAuth0Base;
  useAutoRetryOnError: typeof useAutoRetryOnErrorBase;
  useFeatureGate: typeof useFeatureGateBase;
  useGetClientRequiresAuthedIntroRoom: typeof useGetClientRequiresAuthedIntroRoomBase;
  useGetClientUserMissingConsentsQuery: typeof useGetClientUserMissingConsentsQueryBase;
  useGetClientUserWithParentGuardianInformation: typeof useGetClientUserWithParentGuardianInformationBase;
  useParams: typeof useParamsBase;
  useTrackIntroRoomEvent: typeof useTrackIntroRoomEventBase;
};

/**
 * Custom hook for managing the current state of the intro room.
 *
 * @param flowId - The current unique ID of the client's trial of the intro room
 * flow. This is used to track the client's progress through the flow in Segment.
 * @param dependencies - Optional overrides for the dependencies of the hook.
 * @returns The current intro room state and a callback to progress the state
 * when PII verification is successful.
 */
export function useIntroRoomState(
  flowId: string,
  {
    logger,
    useAuth0,
    useAutoRetryOnError,
    useFeatureGate,
    useGetClientRequiresAuthedIntroRoom,
    useGetClientUserMissingConsentsQuery,
    useGetClientUserWithParentGuardianInformation,
    useParams,
    useTrackIntroRoomEvent,
  }: Dependencies = {
    logger: loggerBase,
    useAuth0: useAuth0Base,
    useAutoRetryOnError: useAutoRetryOnErrorBase,
    useFeatureGate: useFeatureGateBase,
    useGetClientRequiresAuthedIntroRoom:
      useGetClientRequiresAuthedIntroRoomBase,
    useGetClientUserMissingConsentsQuery:
      useGetClientUserMissingConsentsQueryBase,
    useGetClientUserWithParentGuardianInformation:
      useGetClientUserWithParentGuardianInformationBase,
    useParams: useParamsBase,
    useTrackIntroRoomEvent: useTrackIntroRoomEventBase,
  },
) {
  const [introRoomState, setIntroRoomState] =
    useState<IntroRoomState>("loading");

  const { patientShortId } = useParams();
  const { isAuthenticated } = useAuth0();
  const { userViewed } = useTrackIntroRoomEvent();

  const { isLoading, isMissingData, requiresWaitingRoomBlock } =
    useIntroRoomData(
      { flowId, shouldRetryFailedQueries: true },
      {
        logWarning: logger.warn,
        useAuth0,
        useAutoRetryOnError,
        useFeatureGate,
        useGetClientRequiresAuthedIntroRoom,
        useGetClientUserMissingConsentsQuery,
        useGetClientUserWithParentGuardianInformation,
      },
    );

  useEffect(
    function setStateBasedOnMissingConsents() {
      if (isLoading) return;
      if (!requiresWaitingRoomBlock) {
        if (isMissingData) {
          logger.warn(
            "Missing data for unauthenticated client, showing default intro room",
            { flowId, patientShortId },
          );
        }
        userViewed(EventTypeEnum.CLIENT_NEW_INTRO_ROOM_VIEWED, {
          flowId,
          // Indicate if we were unable to verify that the client had all
          // required consents on file.
          isVerified: !isMissingData,
        });
        setIntroRoomState("default");
      } else if (isAuthenticated) {
        userViewed(
          EventTypeEnum.CLIENT_NEW_INTRO_ROOM_REQUIRED_AGREEMENTS_REVIEW_VIEWED,
          { flowId, isClientUserAuthenticated: true },
        );
        setIntroRoomState("requiredAgreementsReview");
      } else {
        userViewed(
          EventTypeEnum.CLIENT_NEW_INTRO_ROOM_PII_VERIFICATION_VIEWED,
          { flowId },
        );
        setIntroRoomState("piiVerification");
      }
    },
    [
      logger,
      flowId,
      isAuthenticated,
      isLoading,
      isMissingData,
      patientShortId,
      requiresWaitingRoomBlock,
      userViewed,
    ],
  );

  const onPIIVerified = useCallback(() => {
    userViewed(
      EventTypeEnum.CLIENT_NEW_INTRO_ROOM_REQUIRED_AGREEMENTS_REVIEW_VIEWED,
      { flowId, isClientUserAuthenticated: false },
    );
    setIntroRoomState("requiredAgreementsReview");
  }, [flowId, userViewed]);

  return {
    introRoomState,
    onPIIVerified,
  };
}
